import type { IntlShape } from 'react-intl';

import { expValEquals } from '@confluence/feature-experiments';
import { getRelativeTime } from '@confluence/macro-utils/entry-points/getRelativeTime.ts';

import type { useBlogPostsQueryQuery$variables } from '../hooks/__generated__/useBlogPostsQueryQuery.graphql.ts';
import type { BlogPostsMacroParams, BlogPostNode } from '../blogPostMacroTypes.ts';

const DAY = 86400000;

const DEFAULT_MAX = 100;
const MAX_LIMIT = 200;

/**
 * Parses a relative time string like "7d", "14d", "1w", "2h" etc.
 * and returns milliseconds.
 */
const parseRelativeTime = (time: string): number | null => {
	const match = time.match(/^(\d+)([smhdwMy])$/);
	if (!match) {
		return null;
	}
	const value = parseInt(match[1], 10);
	const unit = match[2];
	const multipliers: Record<string, number> = {
		s: 1000,
		m: 60 * 1000,
		h: 60 * 60 * 1000,
		d: 24 * 60 * 60 * 1000,
		w: 7 * 24 * 60 * 60 * 1000,
		M: 30 * 24 * 60 * 60 * 1000,
		y: 365 * 24 * 60 * 60 * 1000,
	};
	return value * (multipliers[unit] ?? 0);
};

/**
 * Builds a CQL query string from the macro parameters.
 */
export const buildCqlQuery = (params: BlogPostsMacroParams, currentSpaceKey?: string): string => {
	const clauses: string[] = ["type = 'blogpost'"];

	// Space filter
	const spacesValue = params.spaces?.value ?? params.space?.value;
	if (spacesValue) {
		const spaceKeys = spacesValue.split(',').flatMap((s) => {
			const trimmed = s.trim();
			return trimmed ? [trimmed] : [];
		});
		const includeSpaces: string[] = [];
		const excludeSpaces: string[] = [];

		for (const key of spaceKeys) {
			if (key === '@all') {
				// No space filter needed
				includeSpaces.length = 0;
				excludeSpaces.length = 0;
				break;
			} else if (key === '@self' && currentSpaceKey) {
				includeSpaces.push(currentSpaceKey);
			} else if (key.startsWith('-')) {
				excludeSpaces.push(key.slice(1));
			} else {
				includeSpaces.push(key);
			}
		}

		if (includeSpaces.length > 0) {
			const spaceList = includeSpaces.map((s) => `'${s}'`).join(',');
			clauses.push(`space IN (${spaceList})`);
		}
		for (const excludeSpace of excludeSpaces) {
			clauses.push(`space != '${excludeSpace}'`);
		}
	} else if (currentSpaceKey) {
		// Default: current space
		clauses.push(`space = '${currentSpaceKey}'`);
	}

	// Labels filter
	const labelsValue = params.labels?.value ?? params.label?.value;
	const matchLabels = params['match-labels']?.value ?? 'any';
	if (labelsValue) {
		const labels = labelsValue.split(',').flatMap((l) => {
			const trimmed = l.trim();
			return trimmed ? [trimmed] : [];
		});
		const requireLabels: string[] = [];
		const excludeLabels: string[] = [];
		const anyLabels: string[] = [];

		for (const label of labels) {
			if (label.startsWith('+')) {
				requireLabels.push(label.slice(1));
			} else if (label.startsWith('-')) {
				excludeLabels.push(label.slice(1));
			} else {
				anyLabels.push(label);
			}
		}

		// Required labels (AND)
		for (const label of requireLabels) {
			clauses.push(`label = '${label}'`);
		}

		// Excluded labels
		for (const label of excludeLabels) {
			clauses.push(`label != '${label}'`);
		}

		// Any labels (OR) - or if match-labels is "all", treat as AND
		if (anyLabels.length > 0) {
			if (matchLabels === 'all') {
				for (const label of anyLabels) {
					clauses.push(`label = '${label}'`);
				}
			} else {
				const labelList = anyLabels.map((l) => `'${l}'`).join(',');
				clauses.push(`label IN (${labelList})`);
			}
		}
	}

	// Time filter
	const timeValue = params.time?.value;
	if (timeValue) {
		const ms = parseRelativeTime(timeValue);
		if (ms) {
			// Ensures date calculation matches in SPA context, if SSR rendered,
			// such that resulting cql matches
			const currentTime = window['__SSR_CURRENT_DATE__'] || Date.now();
			const then = new Date(currentTime - ms);
			clauses.push(`lastModified >= '${then.toISOString().split('T')[0]}'`);
		}
	}

	// Author filter
	const authorValue = params.author?.value;
	if (authorValue) {
		const authors = authorValue.split(',').flatMap((a) => {
			const trimmed = a.trim();
			return trimmed ? [trimmed] : [];
		});
		if (authors.length === 1) {
			clauses.push(`creator = '${authors[0]}'`);
		} else if (authors.length > 1) {
			const authorList = authors.map((a) => `'${a}'`).join(',');
			clauses.push(`creator IN (${authorList})`);
		}
	}

	// Sort
	const sortValue = params.sort?.value;
	const reverseValue = params.reverse?.value === 'true';
	let orderClause = 'ORDER BY created DESC'; // default

	if (sortValue === 'title') {
		orderClause = `ORDER BY title ${reverseValue ? 'DESC' : 'ASC'}`;
	} else if (sortValue === 'creation') {
		orderClause = `ORDER BY created ${reverseValue ? 'ASC' : 'DESC'}`;
	} else if (sortValue === 'modified') {
		orderClause = `ORDER BY lastModified ${reverseValue ? 'ASC' : 'DESC'}`;
	} else if (reverseValue) {
		orderClause = 'ORDER BY created ASC';
	}

	return `${clauses.join(' AND ')} ${orderClause}`;
};

/**
 * Builds the GraphQL query variables for the blog posts macro query from
 * macro parameters. Shared between the runtime React hook and the SSR
 * preload handler so the logic is defined in a single place.
 */
export const buildQueryVariables = (
	params: BlogPostsMacroParams,
	currentSpaceKey?: string,
): useBlogPostsQueryQuery$variables => {
	const maxValue = params.max?.value ?? params.maxResults?.value ?? String(DEFAULT_MAX);
	const max = Math.min(parseInt(maxValue, 10) || DEFAULT_MAX, MAX_LIMIT);
	const cql = buildCqlQuery(params, currentSpaceKey);
	const includeRenderedBody =
		!expValEquals('cc_migrated_blog_posts_macro_renders_adf_bodies', 'isEnabled', true) &&
		params.content?.value === 'entire';
	const includeAdfBody =
		expValEquals('cc_migrated_blog_posts_macro_renders_adf_bodies', 'isEnabled', true) &&
		params.content?.value === 'entire';

	return {
		cql,
		first: max,
		includeRenderedBody,
		includeAdfBody,
	};
};

export const isBlogPostNode = (node: BlogPostNode | null | undefined): node is BlogPostNode =>
	node != null;

export const getContentUrl = (node: BlogPostNode): string => {
	const links = node.content?.links;
	if (links?.webui) {
		return `/wiki${links.webui}`;
	}
	return '#';
};

export const getSpaceUrl = (node: BlogPostNode): string => {
	const spaceKey = node.content?.space?.key;
	if (spaceKey) {
		return `/wiki/spaces/${spaceKey}`;
	}
	return '#';
};

export const getAuthorName = (node: BlogPostNode): string =>
	node.content?.history?.createdBy?.displayName ?? 'Unknown';

export const getAuthorAccountId = (node: BlogPostNode): string | undefined => {
	const createdBy = node.content?.history?.createdBy;
	if (createdBy && 'accountId' in createdBy) {
		return createdBy.accountId ?? undefined;
	}
	return undefined;
};

export const getProfileUrl = (node: BlogPostNode): string => {
	const accountId = getAuthorAccountId(node);
	if (accountId) {
		return `/wiki/people/${accountId}?ref=confluence`;
	}
	return '#';
};

export const getAuthorAvatarUrl = (node: BlogPostNode): string | undefined => {
	const createdBy = node.content?.history?.createdBy;
	if (createdBy && 'profilePicture' in createdBy) {
		return createdBy.profilePicture?.path ?? undefined;
	}
	return undefined;
};

export const getCreatedDate = (node: BlogPostNode): string =>
	node.content?.history?.createdDate ?? '';

export const getLabels = (node: BlogPostNode): string[] => {
	const result: string[] = [];
	const nodes = node.content?.labels?.nodes;
	if (!nodes) {
		return result;
	}
	for (const label of nodes) {
		if (label?.name) {
			result.push(label.name);
		}
	}
	return result;
};

export const getLabelUrl = (label: string, spaceKey?: string | null): string => {
	if (spaceKey) {
		return `/wiki/label/${spaceKey}/${encodeURIComponent(label)}`;
	}
	return `/wiki/label/${encodeURIComponent(label)}`;
};

export const formatBlogDate = (dateString: string, intl: IntlShape): string => {
	const date = new Date(dateString);
	return intl.formatDate(date, {
		month: 'short',
		day: 'numeric',
		year: 'numeric',
	});
};

/**
 * Returns a relative time string (e.g. "3 hours ago") for dates less than 1 day old,
 * otherwise falls back to an absolute formatted date (e.g. "Jan 15, 2025").
 *
 * This mirrors the legacy BlogPostsMacro behavior where FriendlyDateFormatter only
 * uses relative time for recent content (~1 day), then switches to a short date.
 */
export const formatSmartDate = (dateString: string, intl: IntlShape): string => {
	const timestamp = new Date(dateString).getTime();
	const diff = Date.now() - timestamp;

	if (diff < DAY) {
		return getRelativeTime(timestamp, intl);
	}

	return formatBlogDate(dateString, intl);
};
